import React, { createContext, useContext, useState } from "react";

const AuthContext = createContext(null);

const DEFAULT_ADDRESS = {
  id: "",
  name: "",
  phone: "",
  line1: "",
  line2: "",
  city: "",
  district: "",
  state: "",
  pincode: "",
  country: "India",
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem("hvac_user");
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      return {
        ...parsed,
        address: {
          ...DEFAULT_ADDRESS,
          ...(parsed.address || {}),
        },
        addresses: parsed.addresses || [],
      };
    } catch {
      return null;
    }
  });

  /* ─── LOGIN ─────────────────────────────── */
  const login = (userData) => {
    const userObj = {
      name: userData.name || "User",
      email: userData.email || "",
      phone: userData.phone || "",
      avatar: userData.avatar || null,
      age: userData.age || "",
      gender: userData.gender || "",
      dob: userData.dob || "",
      notifs: userData.notifs || {},
      address: {
        ...DEFAULT_ADDRESS,
        ...(userData.address || {}),
      },
      addresses: userData.addresses || [],
      ...userData,
    };
    localStorage.setItem("hvac_user", JSON.stringify(userObj));
    setUser(userObj);
  };

  /* ─── LOGOUT ────────────────────────────── */
  const logout = () => {
    localStorage.removeItem("hvac_user");
    localStorage.removeItem("cart");                        // clear cart on logout
    localStorage.setItem("proceed_to_payout", "false");    // reset flag — never redirect to payout after a fresh login
    window.dispatchEvent(new Event("cartUpdated"));         // reset header badge immediately
    setUser(null);
  };

  /* ─── UPDATE USER ───────────────────────── */
  const updateUser = (fields) => {
    const updated = {
      ...user,
      ...fields,
      address: {
        ...(user?.address || DEFAULT_ADDRESS),
        ...(fields?.address || {}),
      },
    };
    localStorage.setItem("hvac_user", JSON.stringify(updated));
    setUser(updated);
  };

  /*
   * saveFirstAddress
   * Called ONLY when the user had zero address in settings
   * and fills one for the first time via PayoutPage.
   * After this, PayoutPage never touches settings again.
   */
  const saveFirstAddress = (addressData) => {
    const hasExisting =
      user?.address?.line1 && user.address.line1.trim() !== "";
    if (hasExisting) return; // safety guard — never overwrite existing
    const updated = {
      ...user,
      address: {
        ...DEFAULT_ADDRESS,
        ...addressData,
      },
    };
    localStorage.setItem("hvac_user", JSON.stringify(updated));
    setUser(updated);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        updateUser,
        saveFirstAddress,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};